import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import CareerModal from '../components/CareerModal';
import '../styles/pages/Careers.css';

const Careers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [careerModalOpen, setCareerModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState({
    title: '',
    id: ''
  });

  const openCareerModal = (positionTitle = '', positionId = '') => {
    setSelectedPosition({
      title: positionTitle,
      id: positionId
    });
    setCareerModalOpen(true);
  };

  // Check URL parameters on mount and when location changes
  useEffect(() => {
    const apply = searchParams.get('apply');
    const position = searchParams.get('position');
    const positionId = searchParams.get('id');
    
    if (apply === 'true') {
      openCareerModal(position || 'General Application', positionId || 'general');
    }
  }, [location.search, searchParams]);

  const closeCareerModal = () => {
    setCareerModalOpen(false);
    setSelectedPosition({
      title: '',
      id: ''
    });
    // Remove query parameters from URL when closing
    if (searchParams.get('apply')) {
      setSearchParams({});
    }
  };

  return (
    <div className="careers">
      {/* Hero Section */}
      <section style={{
        minHeight: "60vh",
        display: "flex", 
        alignItems: "center",
        position: "relative",
        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 85, 247, 0.1) 100%)",
        paddingTop: "120px"
      }}>
        <div className="careers-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center" }}
          >
            <div className="hero-badge">
              💼 Join Our Team
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Build Your <span class=\"gradient-text\">Career</span><br />With Innovation" }}
            />
            
            <p className="hero-subtitle">
              Build your career with a dynamic team of professionals who are passionate about innovation
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openCareerModal('General Application', 'general')}
              >
                Apply Now
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="careers-section">
        <div className="careers-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="careers-section-subtitle">
              Why top talent chooses to grow their careers with us
            </p>
            
            <div className="careers-services-grid">
              <div className="careers-benefit-card">
                <h3>💼 Growth Opportunities</h3>
                <p>Continuous learning and career advancement with clear promotion paths and skill development programs.</p>
              </div>
              
              <div className="careers-benefit-card">
                <h3>🏠 Remote Flexibility</h3>
                <p>Work from anywhere with our hybrid model that supports work-life balance and productivity.</p>
              </div>
              
              <div className="careers-benefit-card">
                <h3>🎯 Competitive Benefits</h3>
                <p>Comprehensive benefits package including health insurance, retirement plans, and performance bonuses.</p>
              </div>
              
              <div className="careers-benefit-card">
                <h3>🌟 Innovation Culture</h3>
                <p>Be part of cutting-edge projects and collaborate with industry experts on transformative solutions.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="careers-section">
        <div className="careers-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <p className="careers-section-subtitle">
              Current opportunities to join our growing team
            </p>
            
            <div className="careers-services-grid">
              <div className="careers-glass-card">
                <h3>Senior Full Stack Developer</h3>
                <p>Join our development team to build cutting-edge applications using modern technologies and frameworks.</p>
                <ul>
                  <li>Remote • Full-time</li>
                  <li>$80k-120k</li>
                  <li>React, Node.js, AWS</li>
                </ul>
                <button 
                  className="btn-primary careers-position-button"
                  onClick={() => openCareerModal('Senior Full Stack Developer', 'dev-001')}
                >
                  Apply Now
                </button>
              </div>
              
              <div className="careers-glass-card">
                <h3>Financial Analyst</h3>
                <p>Analyze financial data and provide strategic insights to help clients optimize their business operations.</p>
                <ul>
                  <li>Hybrid • Full-time</li>
                  <li>$60k-85k</li>
                  <li>Excel, SQL, Tableau</li>
                </ul>
                <button 
                  className="btn-primary careers-position-button"
                  onClick={() => openCareerModal('Financial Analyst', 'fin-001')}
                >
                  Apply Now
                </button>
              </div>
              
              <div className="careers-glass-card">
                <h3>DevOps Engineer</h3>
                <p>Build and maintain scalable infrastructure solutions using cloud technologies and automation tools.</p>
                <ul>
                  <li>Remote • Full-time</li>
                  <li>$90k-130k</li>
                  <li>AWS, Docker, Kubernetes</li>
                </ul>
                <button 
                  className="btn-primary careers-position-button"
                  onClick={() => openCareerModal('DevOps Engineer', 'devops-001')}
                >
                  Apply Now
                </button>
              </div>
              
              <div className="careers-glass-card">
                <h3>Accounting Specialist</h3>
                <p>Handle bookkeeping and financial reporting for clients with accuracy and attention to detail.</p>
                <ul>
                  <li>Hybrid • Full-time</li>
                  <li>$45k-65k</li>
                  <li>QuickBooks, Excel</li>
                </ul>
                <button 
                  className="btn-primary careers-position-button"
                  onClick={() => openCareerModal('Accounting Specialist', 'acc-001')}
                >
                  Apply Now
                </button>
              </div>
            </div>

            <div className="careers-footer-section">
              <p className="careers-footer-text">Don't see a position that fits? We're always looking for talented individuals!</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Career Modal */}
      <CareerModal
        isOpen={careerModalOpen}
        onClose={closeCareerModal}
        positionTitle={selectedPosition.title}
        positionId={selectedPosition.id}
      />
    </div>
  );
};

export default Careers; 